import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { PhotoIcon, XMarkIcon, ArrowUpTrayIcon, MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';

/**
 * ThumbnailUpload - reusable drop-zone thumbnail uploader with preview and zoom modal.
 *
 * Props:
 *  - currentUrl    (string|null) - URL of the existing thumbnail (e.g. from S3)
 *  - file          (File|null)   - currently selected new file (controlled)
 *  - onChange      (file|null)   - called when a file is added or removed
 *  - disabled      (boolean)
 *  - accept        (object)      - mime map, defaults to images
 *  - label         (string)      - field label, defaults to "Thumbnail"
 *  - className     (string)      - extra wrapper classes
 */
export default function ThumbnailUpload({
  currentUrl = null,
  file = null,
  onChange,
  disabled = false,
  accept = { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'] },
  label = 'Thumbnail',
  className = '',
}) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [zoomOpen, setZoomOpen] = useState(false);

  // Build object URL for the new file
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onDrop = useCallback(
    (accepted) => {
      if (accepted.length > 0) {
        onChange(accepted[0]);
      }
    },
    [onChange],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles: 1,
    disabled,
    multiple: false,
  });

  const displayUrl = previewUrl || currentUrl;
  const hasImage = !!displayUrl;

  const handleRemove = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      )}

      <div className="flex items-start gap-4">
        {/* Preview thumbnail */}
        {hasImage && (
          <div className="relative group shrink-0">
            <img
              src={displayUrl}
              alt="Thumbnail preview"
              className="h-20 w-20 object-contain rounded-lg border border-slate-200 bg-white cursor-pointer"
              onClick={() => setZoomOpen(true)}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            {/* Zoom overlay */}
            <button
              type="button"
              onClick={() => setZoomOpen(true)}
              className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 rounded-lg transition-colors"
            >
              <MagnifyingGlassPlusIcon className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
            {/* Remove button */}
            {!disabled && file && (
              <button
                type="button"
                onClick={handleRemove}
                className="absolute -top-1.5 -right-1.5 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 shadow-sm transition-colors"
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        )}

        {/* Dropzone */}
        <div
          {...getRootProps()}
          className={`
            flex-1 flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors
            ${isDragActive
              ? 'border-blue-400 bg-blue-50'
              : disabled
                ? 'border-slate-200 bg-slate-50 cursor-not-allowed'
                : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50/50'
            }
          `}
        >
          <input {...getInputProps()} />
          {isDragActive ? (
            <>
              <ArrowUpTrayIcon className="h-6 w-6 text-blue-500 shrink-0" />
              <span className="text-sm text-blue-600 font-medium">Drop image here</span>
            </>
          ) : (
            <>
              <PhotoIcon className="h-6 w-6 text-slate-400 shrink-0" />
              <div className="min-w-0">
                {file ? (
                  <p className="text-sm text-slate-700 truncate">{file.name}</p>
                ) : (
                  <p className="text-sm text-slate-500">
                    <span className="font-medium text-blue-600">Click to upload</span> or drag and drop
                  </p>
                )}
                <p className="text-xs text-slate-400 mt-0.5">PNG, JPG, GIF, SVG, WebP</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Zoom modal */}
      {zoomOpen && displayUrl && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-8"
          onClick={() => setZoomOpen(false)}
        >
          <div className="relative max-w-3xl max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
            <img
              src={displayUrl}
              alt="Thumbnail zoomed"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            <button
              type="button"
              onClick={() => setZoomOpen(false)}
              className="absolute -top-3 -right-3 bg-white hover:bg-slate-100 text-slate-700 rounded-full p-1.5 shadow-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
